import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Building2, X, Calendar } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { api, friendlyError } from '../lib/api';

const PER_PAGE = 24;

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'preventa', label: 'Preventa' },
  { value: 'en_construccion', label: 'En construcción' },
  { value: 'entrega', label: 'En entrega' },
  { value: 'entregado', label: 'Entregado' },
];

const COUNTRY_OPTIONS = [
  { value: '', label: 'Todos los países' },
  { value: 'CO', label: 'Colombia' },
  { value: 'PA', label: 'Panamá' },
  { value: 'US', label: 'Estados Unidos' },
  { value: 'AE', label: 'Emiratos Árabes' },
];

function fmt(amount, currency) {
  if (amount == null) return '';
  const cur = currency || 'COP';
  try {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${cur} ${amount.toLocaleString('es-CO')}`;
  }
}

export default function ProjectsDirectory() {
  const [sp, setSp] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState(sp.get('q') || '');
  const [city, setCity] = useState(sp.get('city') || '');
  const [country, setCountry] = useState(sp.get('country') || '');
  const [status, setStatus] = useState(sp.get('status') || '');

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const data = await api.listProjects({
        q: sp.get('q'),
        city: sp.get('city'),
        country: sp.get('country'),
        status: sp.get('status'),
        limit: PER_PAGE,
      });
      setProjects(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(friendlyError(e, 'No pudimos cargar los proyectos.'));
    } finally {
      setLoading(false);
    }
  }, [sp]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const apply = () => {
    const next = new URLSearchParams();
    if (q.trim()) next.set('q', q.trim());
    if (city.trim()) next.set('city', city.trim());
    if (country) next.set('country', country);
    if (status) next.set('status', status);
    setSp(next);
  };

  const clearAll = () => {
    setQ(''); setCity(''); setCountry(''); setStatus('');
    setSp(new URLSearchParams());
  };

  const hasFilters = !!(sp.get('q') || sp.get('city') || sp.get('country') || sp.get('status'));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Proyectos Nuevos en habita.co',
    description: 'Proyectos inmobiliarios en preventa, construcción y entrega en Colombia y la región.',
    numberOfItems: total,
  };

  return (
    <div className="min-h-screen bg-habita-cream">
      <SEO
        title="Proyectos Nuevos · Preventa, construcción y entrega"
        description="Descubre proyectos inmobiliarios nuevos en Colombia, Panamá y la región. Constructoras verificadas con inventario en tiempo real, planos, amenidades y rentabilidades estimadas."
        jsonLd={jsonLd}
      />
      <Header />

      <section className="bg-habita-dark text-white py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-white/10 text-xs font-bold uppercase tracking-widest">
            <Building2 className="w-3.5 h-3.5" /> Demo · Proyectos Nuevos
          </div>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold">Proyectos en preventa y construcción</h1>
          <p className="text-white/80 mt-3 max-w-2xl">
            Inventario actualizado de constructoras verificadas. Compara amenidades, precios y rentabilidades. Conecta directamente con la constructora.
          </p>

          <form
            onSubmit={(e) => { e.preventDefault(); apply(); }}
            className="mt-6 bg-white text-habita-dark rounded-2xl p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center"
            data-testid="projects-directory-filters"
          >
            <div className="md:col-span-4 flex items-center gap-2 px-3 min-w-0">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Nombre del proyecto…"
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="projects-directory-q"
              />
            </div>
            <div className="md:col-span-3 flex items-center gap-2 px-3 md:border-l border-gray-200 min-w-0">
              <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                value={city}
                onChange={e => setCity(e.target.value)}
                placeholder="Ciudad"
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="projects-directory-city"
              />
            </div>
            <div className="md:col-span-2 px-3 md:border-l border-gray-200">
              <select
                value={country}
                onChange={e => setCountry(e.target.value)}
                className="w-full h-10 bg-transparent border-0 focus:outline-none text-sm text-habita-dark"
                data-testid="projects-directory-country"
              >
                {COUNTRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 px-3 md:border-l border-gray-200">
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="w-full h-10 bg-transparent border-0 focus:outline-none text-sm text-habita-dark"
                data-testid="projects-directory-status"
              >
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-1 px-2">
              <Button type="submit" className="btn-habita rounded-full px-5 h-10 w-full" data-testid="projects-directory-apply">Buscar</Button>
            </div>
          </form>

          {hasFilters && (
            <button onClick={clearAll} className="text-xs text-white/70 hover:text-white mt-3 inline-flex items-center gap-1" data-testid="projects-directory-clear">
              <X className="w-3 h-3" /> Limpiar filtros
            </button>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <p className="text-sm text-gray-600 mb-5" data-testid="projects-directory-count">
          {loading ? 'Cargando…' : `${total} ${total === 1 ? 'proyecto' : 'proyectos'}`}
        </p>

        {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm" data-testid="projects-directory-error">{err}</div>}

        {!loading && projects.length === 0 && !err && (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center" data-testid="projects-directory-empty">
            <p className="font-serif text-xl font-bold text-habita-dark">Aún no hay proyectos que coincidan</p>
            <p className="text-sm text-gray-600 mt-2">Prueba ajustando los filtros.</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map(p => <ProjectCard key={p.id} p={p} />)}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ProjectCard({ p }) {
  return (
    <Link
      to={`/proyecto/${p.slug}`}
      className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-habita transition-all group"
      data-testid={`project-card-${p.id}`}
    >
      <div className="h-40 bg-habita-mint relative">
        {p.cover_url
          ? <img src={p.cover_url} alt={p.name} className="w-full h-full object-cover" />
          : p.gallery?.[0]
            ? <img src={p.gallery[0]} alt={p.name} className="w-full h-full object-cover" />
            : <div className="w-full h-full bg-gradient-to-br from-habita to-emerald-600 flex items-center justify-center text-white">
                <Building2 className="w-10 h-10 opacity-60" />
              </div>}
        {p.status && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-white/95 text-habita-dark text-[10px] font-bold uppercase tracking-wider" data-testid={`project-card-status-${p.id}`}>
            {p.status.replace('_', ' ')}
          </span>
        )}
        {p.country && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/40 text-white text-[10px] font-bold uppercase tracking-wider backdrop-blur">
            {p.country}
          </span>
        )}
      </div>
      <div className="p-5">
        <p className="font-serif font-bold text-habita-dark text-lg group-hover:text-habita transition-colors truncate">{p.name}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1.5"><Building2 className="w-3 h-3" /> {p.developer_name}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {p.city}, {p.country}</p>
        {p.delivery_date && (
          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Calendar className="w-3 h-3" /> Entrega {p.delivery_date}</p>
        )}
        {p.price_min != null && (
          <p className="text-sm font-semibold text-habita mt-3" data-testid={`project-card-price-${p.id}`}>
            Desde <span className="font-bold">{fmt(p.price_min, p.currency)}</span>
          </p>
        )}
        {p.yield_estimate != null && (
          <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-habita-mint text-habita">
            Renta est. {p.yield_estimate}%
          </span>
        )}
      </div>
    </Link>
  );
}
